import React, { useCallback, useEffect, useState } from 'react';
import { TopBar, Spinner } from '../components/ui';
import { rupee, fmtDate } from '../theme';
import { useToast } from '../context/ToastContext';
import { shareText } from '../utils/download';
import api from '../api/client';

const Stat = ({ label, value }) => (
  <div className="card" style={{ flex: 1, padding: '16px 8px', textAlign: 'center' }}>
    <div style={{ fontSize: 18, fontWeight: 900, color: 'var(--primary)' }}>{value}</div>
    <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>{label}</div>
  </div>
);

const Empty = ({ text }) => (
  <div style={{ padding: 24, textAlign: 'center' }}>
    <span className="faint" style={{ fontSize: 13.5 }}>{text}</span>
  </div>
);

/* ------------------------------- Report ------------------------------- */
function ReportTab({ perJoinAmount }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/mlm/analytics').then(setData).catch(() => setData({ members: [], byDay: [] }));
  }, []);

  if (!data) return <Spinner />;

  return (
    <div style={{ padding: 18, paddingBottom: 40 }}>
      <div className="row" style={{ gap: 10, marginBottom: 20, alignItems: 'stretch' }}>
        <Stat label="Per join" value={rupee(perJoinAmount)} />
        <Stat label="Direct joins" value={data.directJoins ?? 0} />
        <Stat label="Total team" value={data.teamCount ?? 0} />
      </div>

      <h3 style={{ fontSize: 14, fontWeight: 800, marginTop: 8, marginBottom: 10 }}>Joins by date</h3>
      {(data.byDay || []).length === 0 ? <Empty text="No activity yet" /> : data.byDay.map((d) => (
        <div key={d.date} className="card row" style={{ padding: 12, marginBottom: 8 }}>
          <span className="grow" style={{ fontSize: 13.5, fontWeight: 700 }}>{fmtDate(d.date)}</span>
          <span className="muted" style={{ fontSize: 12, marginRight: 12 }}>
            {d.count} entr{d.count === 1 ? 'y' : 'ies'}
          </span>
          <span style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--success)' }}>{rupee(d.amount)}</span>
        </div>
      ))}

      <h3 style={{ fontSize: 14, fontWeight: 800, marginTop: 20, marginBottom: 10 }}>Team members</h3>
      {(data.members || []).length === 0 ? <Empty text="No members yet — share your code!" /> : data.members.map((m) => (
        <div key={m._id} className="card row" style={{ gap: 12, padding: 10, marginBottom: 8 }}>
          <span className="avatar" style={{ width: 40, height: 40, background: 'var(--bg)', color: 'var(--text)' }}>
            {m.profilePhoto ? <img src={m.profilePhoto} alt="" /> : (m.name || '?').charAt(0).toUpperCase()}
          </span>
          <div className="grow">
            <div style={{ fontSize: 13.5, fontWeight: 700 }}>{m.name || 'Partner'}</div>
            <div className="faint" style={{ fontSize: 11.5 }}>{m.staffId || ''}</div>
          </div>
          <span className="muted" style={{ fontSize: 11.5 }}>{fmtDate(m.joinedAt)}</span>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------- Team -------------------------------- */
function TreeNode({ node, depth, onDrill }) {
  const hasChildren = (node.children || []).length > 0;
  // A collapsed node at the view's edge that still has members below.
  const collapsedWithMore = !hasChildren && node.directCount > 0;

  return (
    <div style={{ marginLeft: depth ? 16 : 0 }}>
      <div
        onClick={() => collapsedWithMore && onDrill(node)}
        className="card row"
        style={{
          gap: 10, padding: 10, marginBottom: 8,
          border: depth === 0 ? '1.5px solid var(--primary)' : '1px solid var(--border)',
          cursor: collapsedWithMore ? 'pointer' : 'default',
        }}
      >
        <span className="avatar" style={{ width: 38, height: 38 }}>
          {node.profilePhoto ? <img src={node.profilePhoto} alt="" /> : (node.name || '?').charAt(0).toUpperCase()}
        </span>
        <div className="grow">
          <div style={{ fontSize: 13.5, fontWeight: 700 }}>
            {node.name || 'Partner'}{depth === 0 ? ' (You)' : ''}
          </div>
          <div className="faint" style={{ fontSize: 11.5 }}>
            {node.referralCode || ''} · {node.directCount || 0} member{node.directCount === 1 ? '' : 's'}
          </div>
        </div>
        {collapsedWithMore && (
          <span style={{
            background: 'var(--primary-soft)', color: 'var(--primary)', borderRadius: 'var(--r-pill)',
            padding: '5px 10px', fontWeight: 800, fontSize: 12,
          }}>+{node.directCount}</span>
        )}
      </div>

      {hasChildren && (
        <div style={{ borderLeft: '1px solid var(--border)', marginLeft: 18, paddingLeft: 6, marginTop: 2 }}>
          {node.children.map((c) => <TreeNode key={c._id} node={c} depth={depth + 1} onDrill={onDrill} />)}
        </div>
      )}
    </div>
  );
}

// Shows 3 layers at a time; tapping a node with its own members drills in.
function TeamTab() {
  const [rootId, setRootId] = useState(undefined);
  const [path, setPath] = useState([]); // breadcrumb: [{ id, name }]
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (nodeId) => {
    setLoading(true);
    try {
      const q = nodeId ? `?node=${nodeId}&depth=3` : '?depth=3';
      const d = await api.get(`/mlm/tree${q}`);
      setTree(d.tree);
    } catch (e) {
      setTree(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(rootId); }, [rootId, load]);

  const drillInto = (node) => {
    setPath((p) => [...p, { id: node._id, name: node.name }]);
    setRootId(node._id);
  };
  const jumpTo = (idx) => {
    if (idx < 0) { setPath([]); setRootId(undefined); return; }
    const target = path[idx];
    setPath((p) => p.slice(0, idx + 1));
    setRootId(target.id);
  };

  if (loading) return <Spinner />;
  if (!tree) return <Empty text="No network data" />;

  return (
    <div style={{ padding: 16, paddingBottom: 40 }}>
      <div className="row wrap" style={{ marginBottom: 6, gap: 0 }}>
        <button onClick={() => jumpTo(-1)} style={{ color: 'var(--primary)', fontWeight: 800, fontSize: 13 }}>Me</button>
        {path.map((p, i) => (
          <span key={p.id} className="row" style={{ gap: 0 }}>
            <span className="faint" style={{ margin: '0 6px' }}>›</span>
            <button onClick={() => jumpTo(i)} style={{ color: 'var(--primary)', fontWeight: 800, fontSize: 13 }}>
              {p.name || 'Partner'}
            </button>
          </span>
        ))}
      </div>
      <p className="faint" style={{ fontSize: 11.5, marginBottom: 14, marginTop: 0 }}>
        Showing 3 layers. Tap a member with a "+N" badge to open their team.
      </p>

      <TreeNode node={tree} depth={0} onDrill={drillInto} />
    </div>
  );
}

export default function MlmDashboard({ onClose, referralCode, perJoinAmount }) {
  const { toast } = useToast();
  const [tab, setTab] = useState('report'); // 'report' | 'team'

  const doShare = async () => {
    const r = await shareText(`Join my Narayan team with referral code: ${referralCode}`);
    if (r.copied) toast('Copied to clipboard', { tone: 'success' });
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <TopBar title="Partner Dashboard" onClose={onClose} />

      <div className="row" style={{ background: 'var(--primary)', padding: '0 18px 16px', gap: 12 }}>
        <div className="grow">
          <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11.5, fontWeight: 600 }}>Your referral code</div>
          <div style={{ color: '#fff', fontSize: 22, fontWeight: 900, letterSpacing: 2, marginTop: 2 }}>{referralCode}</div>
        </div>
        <button className="btn btn--light btn--sm" onClick={doShare}>Share</button>
      </div>

      <div className="row" style={{ gap: 8, padding: 14, background: 'var(--surface)', boxShadow: 'var(--shadow-card)' }}>
        {[['report', '📊 Report'], ['team', '🌳 My Team']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            flex: 1, padding: '10px 0', borderRadius: 'var(--r-pill)', fontWeight: 700, fontSize: 13.5,
            background: tab === k ? 'var(--primary)' : 'var(--bg)',
            color: tab === k ? '#fff' : 'var(--muted)',
          }}>{l}</button>
        ))}
      </div>

      {tab === 'report' ? <ReportTab perJoinAmount={perJoinAmount} /> : <TeamTab />}
    </div>
  );
}
